////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// aoe-helpers.ts — pure geometry and catalog helpers retained by encounter-map callers.
// ----------------------------------------------------------------------------------------------------
// Bundled into includes/js/dist/encounter-map-bundle.js. Exposed via
// window.FTEncounterMap.aoe.* for callers that need standalone geometry.
//
// Helpers here do not touch `this`, DOM, or fetch. Shared geometry also lives in ./grid-math.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { FT3DPoint } from './grid-math';


// SOURCE: includes/js/template.js:28393-28395 (AOEManager._calcEdgeX).
// Cube / square AOEs are centered on the caster (half-extent past center on each side);
// circle / cone / line variants extend by the full distance from center to edge.
export function calcEdgeX(varName: string, cx: number, dist: number): number {
    return (varName === 'cube' || varName === 'square') ? cx + dist / 2 : cx + dist;
}


// SOURCE: includes/js/template.js:28397-28404 (AOEManager._parseSelectionPoint).
// Parse the encoded selection-point string from a click/drag handler.
// Format: "<prefix>|<x>:<z>" — pipe-separated head, then colon-separated coords.
// Returns null for any malformed or non-finite input.
export function parseSelectionPoint(str: string): FT3DPoint | null {
    const parts = str.split('|');
    if (parts.length !== 2) return null;
    const encodedCoords = parts[1];
    if (!encodedCoords) return null;
    const coords = encodedCoords.split(':');
    if (coords.length < 2) return null;
    const x = parseFloat(coords[0] ?? '');
    const z = parseFloat(coords[1] ?? '');
    return Number.isFinite(x) && Number.isFinite(z) ? { x, z } : null;
}


// SOURCE: includes/js/template.js:28366-28371 (AOEManager._tokenHalfWidthFt).
// Half-extent of a token in grid feet given the current tick spacing and the token's
// rendered pixel width. The original signature took (widget, el) and resolved both
// internally; this version is pure and the delegate wrapper resolves them at the call site.
export function tokenHalfWidthFt(tickSpacing: number, elOffsetWidthPx: number): number {
    return (elOffsetWidthPx / tickSpacing) / 2;
}


// Catalog entry shape we read off `rangeTypes`. The full catalog has more fields
// but only these matter for range resolution.
type RangeCatalogEntry = {
    item_id: string;
    x_distance?: number | 'reach' | string;
};

// SOURCE: Phase 1.2-bonus split of the retired 2-D _getRangeDistance.
// Pure catalog half of the lookup: UUID -> numeric distance OR the sentinel 'reach'
// (which means "use the caster's reach attribute from the DOM"). Unlimited becomes
// positive infinity so normal numeric comparisons always keep the placement in range.
// Returns null when the rangeId isn't in the catalog. The 'reach' sentinel remains
// the caller's responsibility.
export function getRangeDistanceFromCatalog(
    rangeId: string,
    catalog: ReadonlyArray<RangeCatalogEntry> | null | undefined,
): number | 'reach' | null {
    const rt = catalog?.find(r => r.item_id === rangeId);
    if (!rt) return null;
    if (typeof rt.x_distance === 'number') return rt.x_distance;
    if (rt.x_distance === 'reach') return 'reach';
    if (rt.x_distance === 'unlimited') return Number.POSITIVE_INFINITY;
    return null;
}
